(function () {
  if (window.dosePixel && window.dosePixel.loaded) return;

  var CLICK_IDS = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid', 'mlclid'];
  var UTMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var VISITOR_COOKIE = '_dose_vid';
  var SESSION_COOKIE = '_dose_sid';
  var SESSION_STORE = 'dose_pixel_session';
  var CLICK_STORE = 'dose_pixel_click_ids';
  var PURCHASE_EVENT_STORE = 'dose_pixel_purchase_event_id';
  var DISABLED_FLAG = 'dose_pixel_disabled';
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  var VISITOR_MAX_AGE = 63072000;
  var SESSION_MAX_AGE = 1800;

  // In-memory opt-out flag for contexts where localStorage is blocked
  // (Safari private browsing, storage quota exceeded, third-party-cookie
  // contexts). Survives the page lifetime even when persistence fails.
  var inMemoryDisabled = false;

  function isDisabled() {
    if (inMemoryDisabled) return true;
    try {
      return Boolean(
        window.localStorage && window.localStorage.getItem(DISABLED_FLAG) === '1'
      );
    } catch {
      return false;
    }
  }

  var script = document.currentScript;
  var endpoint = (
    window.DOSE_PIXEL_ENDPOINT ||
    (script && script.getAttribute('data-endpoint')) ||
    'https://track.takeadoseof.com/functions/v1/pixel-ingest'
  );
  var debug = Boolean(
    window.DOSE_PIXEL_DEBUG ||
    (script && script.getAttribute('data-debug') === 'true')
  );

  // Fingerprint subsystem gates (amendment 02, 2026-05-22).
  // BOTH must be true for the snippet to compute a fingerprint. Defaults
  // are false everywhere; the production theme sets both flags via inline
  // <script> in theme.liquid head, gated on theme settings that flip only
  // after the aviso ships and the privacy checklist is signed off.
  // The Edge env DOSE_FINGERPRINT_ENABLED is set in Supabase (not Vercel)
  // for the pixel-ingest function — see
  // docs/specs/dose-pixel/week-2-fingerprint-rollout-runbook.md §C.
  //
  // Strict parsing: only true, "true", 1, "1" count as on. Naive Boolean()
  // would treat "false" (a non-empty string) as truthy — dangerous when the
  // flag is wired from inline theme/env plumbing that stringifies booleans.
  function enabledFlag(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
  }
  var fingerprintEnabled =
    enabledFlag(window.DOSE_FINGERPRINT_ENABLED) ||
    (script && script.getAttribute('data-fingerprint-enabled') === 'true');
  var privacyChecklistAcknowledged = enabledFlag(window.DOSE_PIXEL_PRIVACY_OK);

  var priorApi = window.dosePixel;
  var queuedCalls = priorApi && Array.isArray(priorApi.q) ? priorApi.q.slice() : [];
  var lastPageKey = null;
  var initialPageTracked = false;

  // Snapshot the _dose_vid cookie state BEFORE ensureVisitorId() runs.
  // The fingerprint gate requires "cookie was missing on page load" — we
  // can't check it later because ensureVisitorId() will have set it.
  var visitorCookieMissingAtLoad = null;
  var fingerprintPayload = null;
  var fingerprintSent = false;
  var fingerprintReady = null;

  function log() {
    if (debug && window.console && console.log) console.log.apply(console, arguments);
  }

  // Mirror server-side UUID v4 regex (pixel-shopify-webhook/logic.ts:341).
  // preparePixelIngest rejects any non-v4 event_id; the cart-attribute
  // read paths (Custom Pixel + server webhook) validate before adopting.
  // Reused here when reading a stored _dose_purchase_event_id from
  // localStorage so a corrupted value regenerates fresh.
  var UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    if (!window.crypto || !crypto.getRandomValues) {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0;
        var v = c === 'x' ? r : (r & 3 | 8);
        return v.toString(16);
      });
    }
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, function (c) {
      return (Number(c) ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> Number(c) / 4).toString(16);
    });
  }

  function cookieDomain() {
    var host = window.location.hostname.toLowerCase();
    if (host === 'takeadoseof.com' || host.endsWith('.takeadoseof.com')) return '.takeadoseof.com';
    return null;
  }

  function getCookie(name) {
    var prefix = name + '=';
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(prefix) === 0) return decodeURIComponent(parts[i].slice(prefix.length));
    }
    return null;
  }

  function setCookie(name, value, maxAge) {
    var parts = [
      name + '=' + encodeURIComponent(value),
      'Path=/',
      'Max-Age=' + maxAge,
      'SameSite=Lax'
    ];
    var domain = cookieDomain();
    if (domain) parts.push('Domain=' + domain);
    if (window.location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  }

  function readStore(key, fallback) {
    try {
      var raw = window.localStorage && localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  }

  function writeStore(key, value) {
    try {
      if (window.localStorage) localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // localStorage can be blocked in private contexts; cookies still carry IDs.
    }
  }

  function getParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch {
      return null;
    }
  }

  function captureMarketing() {
    var current = {};
    var i;
    for (i = 0; i < UTMS.length; i++) {
      var utmValue = getParam(UTMS[i]);
      if (utmValue) current[UTMS[i]] = utmValue;
    }

    var storedClicks = readStore(CLICK_STORE, {});
    for (i = 0; i < CLICK_IDS.length; i++) {
      var clickValue = getParam(CLICK_IDS[i]);
      if (clickValue) storedClicks[CLICK_IDS[i]] = clickValue;
    }
    // First-click timestamp for the click→purchase shadow measurement (handoff
    // 2026-06-07): stamp the first time we hold ANY click id, so the server
    // webhook can size how long after the ad click a purchase happened. Not a
    // CLICK_IDS member, so it never leaks into event marketing fields.
    if (typeof storedClicks._captured_at !== 'number') {
      for (i = 0; i < CLICK_IDS.length; i++) {
        if (storedClicks[CLICK_IDS[i]]) {
          storedClicks._captured_at = Date.now();
          break;
        }
      }
    }
    writeStore(CLICK_STORE, storedClicks);

    for (i = 0; i < CLICK_IDS.length; i++) {
      if (storedClicks[CLICK_IDS[i]]) current[CLICK_IDS[i]] = storedClicks[CLICK_IDS[i]];
    }
    return current;
  }

  function acquisitionKey(marketing) {
    return [
      marketing.utm_source || '',
      marketing.utm_medium || '',
      marketing.utm_campaign || ''
    ].join('|');
  }

  function ensureVisitorId() {
    var visitorId = getCookie(VISITOR_COOKIE);
    if (!visitorId) visitorId = uuid();
    setCookie(VISITOR_COOKIE, visitorId, VISITOR_MAX_AGE);
    return visitorId;
  }

  function ensureSession(marketing) {
    var now = Date.now();
    var stored = readStore(SESSION_STORE, null);
    var storedCookie = getCookie(SESSION_COOKIE);
    var key = acquisitionKey(marketing);
    var expired = !stored || !stored.last_seen || now - stored.last_seen > SESSION_TIMEOUT_MS;
    var acquisitionChanged = stored && stored.acquisition_key !== key && key !== '||';
    var sessionId = storedCookie && stored && !expired && !acquisitionChanged ? stored.session_id : uuid();

    var session = {
      session_id: sessionId,
      acquisition_key: acquisitionChanged || expired || !stored ? key : stored.acquisition_key,
      landing_url: acquisitionChanged || expired || !stored ? window.location.href : stored.landing_url,
      landing_referrer: acquisitionChanged || expired || !stored ? document.referrer || null : stored.landing_referrer,
      marketing: acquisitionChanged || expired || !stored ? marketing : Object.assign({}, stored.marketing || {}, marketing),
      last_seen: now
    };

    writeStore(SESSION_STORE, session);
    setCookie(SESSION_COOKIE, session.session_id, SESSION_MAX_AGE);
    return session;
  }

  function shopifyCustomerId() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    if (meta && meta.page && meta.page.customerId) return String(meta.page.customerId);
    if (window.__st && window.__st.cid) return String(window.__st.cid);
    return null;
  }

  function cartToken() {
    if (window.Shopify && window.Shopify.cartCookie) return String(window.Shopify.cartCookie);
    var cartCookie = getCookie('cart');
    return cartCookie || null;
  }

  function commonFields() {
    var marketing = captureMarketing();
    var session = ensureSession(marketing);
    var fields = {
      event_id: uuid(),
      event_time: new Date().toISOString(),
      event_source: 'browser',
      visitor_id: ensureVisitorId(),
      session_id: session.session_id,
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      screen_width: window.screen && window.screen.width || null,
      screen_height: window.screen && window.screen.height || null,
      viewport_width: window.innerWidth || null,
      language: navigator.language || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      landing_url: session.landing_url,
      landing_referrer: session.landing_referrer,
      cart_token: cartToken(),
      shopify_customer_id: shopifyCustomerId()
    };

    Object.assign(fields, session.marketing || {}, marketing);
    return fields;
  }

  // ---- Fingerprint helpers (amendment 02) -----------------------------------
  // identity-hierarchy.md:109-185 spec. Components, hash, gates. Compute starts
  // as soon as the snippet evaluates so the SubtleCrypto.digest promise has
  // resolved before the initial page_view fires (firePageContext awaits
  // fingerprintReady up to 500ms).

  function uaFamily() {
    var ua = (navigator && navigator.userAgent) || '';
    var m = ua.match(/Edg\/(\d+)/);
    if (m) return 'Edge/' + m[1];
    m = ua.match(/OPR\/(\d+)/);
    if (m) return 'Opera/' + m[1];
    m = ua.match(/Firefox\/(\d+)/);
    if (m) return 'Firefox/' + m[1];
    m = ua.match(/Chrome\/(\d+)/);
    if (m) return 'Chrome/' + m[1];
    if (ua.indexOf('Safari') !== -1) {
      var v = ua.match(/Version\/(\d+)/);
      if (v) return 'Safari/' + v[1];
    }
    return 'Other';
  }

  function bytesToHex(buf) {
    var bytes = new Uint8Array(buf);
    var hex = '';
    for (var i = 0; i < bytes.length; i++) {
      var b = bytes[i].toString(16);
      hex += b.length === 1 ? '0' + b : b;
    }
    return hex;
  }

  function sha256Hex(input) {
    try {
      if (!window.crypto || !window.crypto.subtle || !window.crypto.subtle.digest) {
        return Promise.resolve(null);
      }
      var encoder = new TextEncoder();
      return window.crypto.subtle.digest('SHA-256', encoder.encode(input)).then(bytesToHex).catch(function () {
        return null;
      });
    } catch {
      return Promise.resolve(null);
    }
  }

  function canvasSignature() {
    try {
      var canvas = document.createElement('canvas');
      if (!canvas || typeof canvas.getContext !== 'function') return null;
      var ctx = canvas.getContext('2d');
      if (!ctx) return null;
      canvas.width = 280;
      canvas.height = 60;
      ctx.textBaseline = 'top';
      ctx.font = '14px "Arial"';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('dose-pixel', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('dose-pixel', 4, 17);
      return canvas.toDataURL();
    } catch {
      return null;
    }
  }

  function fingerprintComponents() {
    var dpr = window.devicePixelRatio || 1;
    var sw = (window.screen && window.screen.width) || 0;
    var sh = (window.screen && window.screen.height) || 0;
    var vw = window.innerWidth || 0;
    var vh = window.innerHeight || 0;
    return {
      screen_geometry: sw + 'x' + sh + '@' + dpr,
      viewport_geometry: vw + 'x' + vh,
      ua_family: uaFamily(),
      platform: (navigator && navigator.platform) || null,
      language: (navigator && navigator.language) || null,
      timezone_offset_minutes: new Date().getTimezoneOffset(),
      color_depth: (window.screen && window.screen.colorDepth) || null,
      touch_support: 'ontouchstart' in window
    };
  }

  function shouldComputeFingerprint() {
    if (!fingerprintEnabled) return false;
    if (!privacyChecklistAcknowledged) return false;
    if (isDisabled()) return false;
    if (visitorCookieMissingAtLoad === false) return false;
    if (shopifyCustomerId()) return false;
    return true;
  }

  function buildFingerprintPayload() {
    var comps = fingerprintComponents();
    var signature = canvasSignature();
    // Canvas-required hardening (Step 7): without a canvas signature, the
    // remaining components (screen, viewport, UA family, platform, language,
    // timezone, color depth, touch support) carry far too little entropy to
    // distinguish visitors. A canvas-less fingerprint_hash could exact-match
    // strangers and trigger probabilistic_restored, bypassing the 0.85
    // weighted threshold. When canvas is unavailable, skip emission entirely;
    // the visitor falls back to confidence = "cookie_only" on the Edge.
    if (!signature) return Promise.resolve(null);

    return sha256Hex(signature).then(function (canvasHashHex) {
      if (!canvasHashHex) return null;
      comps.canvas_hash = canvasHashHex;
      // Canonical concatenation order is fixed by spec — changes here
      // invalidate every stored fingerprint_hash. Order intentionally matches
      // identity-hierarchy.md:121-135 storage shape.
      var canonical = [
        comps.canvas_hash,
        comps.screen_geometry || '',
        comps.viewport_geometry || '',
        comps.ua_family || '',
        comps.platform || '',
        comps.language || '',
        String(comps.timezone_offset_minutes),
        String(comps.color_depth),
        String(comps.touch_support)
      ].join('|');
      return sha256Hex(canonical).then(function (hashHex) {
        if (!hashHex) return null;
        comps.fingerprint_hash = hashHex;
        return comps;
      });
    });
  }

  function startFingerprintCompute() {
    if (fingerprintReady) return;
    if (!shouldComputeFingerprint()) {
      fingerprintReady = Promise.resolve(null);
      return;
    }
    // Race the real compute against a 500ms timeout. The initial page_view
    // never waits longer than that — slow devices still get their page_view
    // out the door even if the digest hangs.
    var compute = buildFingerprintPayload().then(function (payload) {
      if (payload) fingerprintPayload = payload;
    }).catch(function () {
      // swallow — fingerprint is best-effort, never breaks the storefront
    });
    var timeout = new Promise(function (resolve) { setTimeout(resolve, 500); });
    fingerprintReady = Promise.race([compute, timeout]);
  }

  function attachFingerprintIfApplicable(payload) {
    if (fingerprintSent) return;
    if (!fingerprintPayload) return;
    payload.fingerprint = fingerprintPayload;
    fingerprintSent = true;
  }

  var MAX_BODY_BYTES = 64 * 1024;

  function byteLength(body) {
    // sendBeacon + fetch+keepalive cap at 64KB BYTES per Fetch spec.
    // JS string .length is char count, not byte count — multibyte payloads
    // (emoji, accented chars) would pass a .length check but fail at the
    // wire. Measure UTF-8 byte length via Blob.size (no TextEncoder allocation
    // overhead on large strings).
    try {
      return new Blob([body]).size;
    } catch {
      // Final fallback when Blob is unavailable: treat each char as 1 byte.
      // Conservative for our use case — only ASCII payloads will under-report,
      // and ASCII char.length === byte count.
      return body.length;
    }
  }

  function send(payload) {
    var body = JSON.stringify(payload);
    log('[dose-pixel]', payload.event_name, payload);

    // Client-side pre-flight: sendBeacon and fetch+keepalive both cap at 64KB
    // per the Fetch spec. Skip the transport entirely on oversize rather than
    // letting the user agent reject silently. Edge Function also rejects
    // oversize with HTTP 413 as belt + braces.
    var bytes = byteLength(body);
    if (bytes > MAX_BODY_BYTES) {
      log('[dose-pixel] payload exceeds 64KB, dropped', payload.event_name, bytes);
      return;
    }

    if (navigator.sendBeacon) {
      try {
        var blob = new Blob([body], { type: 'text/plain' });
        if (navigator.sendBeacon(endpoint, blob)) return;
      } catch {
        // Fall through to fetch.
      }
    }

    try {
      fetch(endpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
        body: body
      }).catch(function () {});
    } catch {
      // Last resort: never break the storefront for analytics.
    }
  }

  function track(eventName, props) {
    // Opt-out gate: when the user has disabled tracking, emit nothing.
    if (isDisabled()) return null;

    props = props || {};
    var payload = commonFields();
    Object.assign(payload, props);
    payload.event_name = eventName;
    payload.event_source = 'browser';
    payload.event_id = props.event_id || payload.event_id || uuid();
    payload.event_time = props.event_time || payload.event_time || new Date().toISOString();

    // trigger_reason is required on browser page_view events only.
    // Other events never carry it; view_pdp inherits from its sibling page_view.
    if (eventName === 'page_view') {
      payload.trigger_reason = props.trigger_reason || 'manual';
    } else {
      delete payload.trigger_reason;
    }

    // Fingerprint attaches at most once per snippet lifetime, when all 5 gates
    // pass + the SHA-256 digest has resolved. Subsequent events go without it.
    attachFingerprintIfApplicable(payload);

    send(payload);
    return payload.event_id;
  }

  function productMeta() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    var product = meta && meta.product;
    if (!product && window.meta && window.meta.product) product = window.meta.product;
    if (!product && window.location.pathname.indexOf('/products/') === 0) {
      return { product_handle: window.location.pathname.split('/products/')[1].split('/')[0] };
    }
    if (!product) return null;
    return {
      product_id: product.id ? String(product.id) : null,
      product_handle: product.handle || null,
      product_title: product.title || null
    };
  }

  function pageKey() {
    return window.location.pathname + window.location.search;
  }

  function firePageContext(force, triggerReason) {
    var key = pageKey();
    if (!force && lastPageKey === key) return;
    lastPageKey = key;
    track('page_view', { trigger_reason: triggerReason });
    var product = productMeta();
    if (product) track('view_pdp', product);
  }

  function fireInitialEvents() {
    if (initialPageTracked) return;
    initialPageTracked = true;
    // Defer the initial page_view briefly so the fingerprint compute promise
    // can resolve and attach to the page_view itself. fingerprintReady is
    // already race-capped at 500ms in startFingerprintCompute, so the
    // worst-case delay is 500ms — well inside Triple Whale's tolerance.
    if (fingerprintReady) {
      fingerprintReady.then(function () {
        firePageContext(true, 'initial');
      });
    } else {
      firePageContext(true, 'initial');
    }
  }

  function startInitialEventsWhenVisible() {
    if (document.visibilityState === 'hidden' || document.visibilityState === 'prerender') {
      var onVisible = function () {
        if (document.visibilityState !== 'visible') return;
        document.removeEventListener('visibilitychange', onVisible);
        fireInitialEvents();
      };
      document.addEventListener('visibilitychange', onVisible);
      return;
    }
    fireInitialEvents();
  }

  function installNavigationHooks() {
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) firePageContext(true, 'pageshow_persisted');
    });

    if (window.__dosePixelHistoryHooked || !window.history) return;
    window.__dosePixelHistoryHooked = true;

    var history = window.history;
    function afterHistoryChange(previousKey, triggerReason) {
      window.setTimeout(function () {
        if (pageKey() !== previousKey) firePageContext(false, triggerReason);
      }, 0);
    }

    function patchHistoryMethod(methodName, triggerReason) {
      var original = history[methodName];
      if (typeof original !== 'function') return;
      history[methodName] = function () {
        var previousKey = pageKey();
        var result = original.apply(this, arguments);
        afterHistoryChange(previousKey, triggerReason);
        return result;
      };
    }

    patchHistoryMethod('pushState', 'pushstate');
    patchHistoryMethod('replaceState', 'replacestate');
    window.addEventListener('popstate', function () {
      firePageContext(false, 'popstate');
    });
  }

  function trackAddToCartFromResponse(item) {
    var added = {};
    if (item && typeof item === 'object') {
      added = {
        sku: item.sku || null,
        product_id: item.product_id ? String(item.product_id) : null,
        variant_id: item.variant_id ? String(item.variant_id) : null,
        quantity: item.quantity || 1,
        unit_price: item.price != null ? String(Number(item.price) / 100) : null,
        title: item.title || item.product_title || null
      };
    }
    track('add_to_cart', { added_item: added });
    // Allocate + write the _dose_purchase_event_id cart attribute on
    // the FIRST add_to_cart of the session. Subsequent adds reuse the
    // stored UUID without re-POSTing /cart/update.js — see
    // ensurePurchaseEventIdInCart for the idempotency story.
    //
    // Why on add_to_cart instead of checkout-link click: Shopify Custom
    // Pixel sandbox cannot write the cart attribute (CORS blocks the
    // sandboxed POST). Writing here, same-origin from the storefront,
    // is the canonical path; cart attributes survive into
    // order.note_attributes on order submission. Custom Pixel still
    // covers the checkout-completed event_id read via
    // checkout.attributes._dose_purchase_event_id.
    ensurePurchaseEventIdInCart();
  }

  function installFetchAddToCartHook() {
    if (!window.fetch || window.__dosePixelFetchHooked) return;
    window.__dosePixelFetchHooked = true;
    var originalFetch = window.fetch;
    window.fetch = function () {
      var input = arguments[0];
      var url = typeof input === 'string' ? input : input && input.url;
      var isCartAdd = url && /\/cart\/add(\.js)?/.test(url);
      return originalFetch.apply(this, arguments).then(function (response) {
        if (isCartAdd && response && response.ok) {
          try {
            response.clone().json().then(trackAddToCartFromResponse).catch(function () {
              track('add_to_cart');
              // Bare-track fallback path STILL needs cart-attribute
              // allocation. trackAddToCartFromResponse handles it on
              // the happy path; mirror here.
              ensurePurchaseEventIdInCart();
            });
          } catch {
            track('add_to_cart');
            ensurePurchaseEventIdInCart();
          }
        }
        return response;
      });
    };
  }

  function isCheckoutHref(href) {
    if (!href) return false;
    try {
      var url = new URL(href, window.location.href);
      return url.pathname === '/checkout' || url.pathname.indexOf('/checkouts/') === 0;
    } catch {
      return false;
    }
  }

  // Read + validate the stored purchase event_id record. Returns the
  // full stored object ({ event_id, created_at, cart_write_confirmed })
  // if the event_id is a UUID v4, null otherwise. A malformed stored
  // value (manual localStorage tampering, prior bug, schema change)
  // is treated as absent so the caller regenerates fresh.
  function readStoredPurchaseEventId() {
    var stored = readStore(PURCHASE_EVENT_STORE, null);
    if (stored && typeof stored === 'object' && typeof stored.event_id === 'string' && UUID_V4_RE.test(stored.event_id)) {
      return stored;
    }
    return null;
  }

  // Idempotent core: ensures _dose_purchase_event_id exists in
  // localStorage AND on the Shopify cart. Returns Promise<UUID | null>.
  //
  // Three states the stored record can be in:
  //   1. Absent / malformed → generate fresh UUID, write pending,
  //      POST cart-update, flip to confirmed on response.ok.
  //   2. Present, cart_write_confirmed === true → reuse UUID, NO POST.
  //   3. Present, cart_write_confirmed !== true → reuse same UUID
  //      (don't rotate), RETRY the POST. Earlier write either
  //      rejected (transient network), returned non-2xx, or never
  //      completed (page navigation mid-fetch). Without retry, the
  //      browser purchase would carry a UUID Shopify never saw and
  //      the server-side webhook would fall back to checkout-token
  //      or server_only — exactly what cart-attribute is meant to
  //      eliminate.
  //
  // Opt-out gate: returns Promise.resolve(null) without writing
  // anything. Repeated calls within the same cart/session reuse the
  // same UUID. Caller decides whether to fire checkout_start downstream.
  //
  // Real-traffic finding 2026-05-28: Shopify Custom Pixel sandbox
  // cannot POST /cart/update.js (CORS-blocked). Theme snippet
  // (this file) is the canonical writer — runs same-origin from
  // the storefront, the POST is just a normal authenticated
  // storefront request. Custom Pixel still reads the attribute
  // via checkout.attributes._dose_purchase_event_id.
  function ensurePurchaseEventIdInCart() {
    if (isDisabled()) return Promise.resolve(null);

    var stored = readStoredPurchaseEventId();
    if (stored && stored.cart_write_confirmed === true) {
      // State 2: confirmed write. Reuse without re-POSTing.
      return Promise.resolve(stored.event_id);
    }

    // State 1 or 3: need to (re)attempt the cart-update POST.
    var purchaseEventId;
    var createdAt;
    if (stored) {
      // State 3: retry with the SAME UUID. Shopify cart may already
      // have it from a partial earlier write; if so, the second POST
      // is a no-op overwrite with the same value, also fine.
      purchaseEventId = stored.event_id;
      createdAt = stored.created_at || new Date().toISOString();
    } else {
      // State 1: fresh allocation. Stamp localStorage as pending so a
      // page navigation mid-fetch doesn't lose the UUID.
      purchaseEventId = uuid();
      createdAt = new Date().toISOString();
      writeStore(PURCHASE_EVENT_STORE, {
        event_id: purchaseEventId,
        created_at: createdAt,
        cart_write_confirmed: false
      });
    }

    var body = new URLSearchParams();
    body.set('attributes[_dose_purchase_event_id]', purchaseEventId);

    // Shadow click→purchase identity (durable-id measurement, handoff
    // 2026-06-07). Ride the storefront browse context onto the order via cart
    // attributes so the server webhook can size the >7-day click→purchase
    // attribution gap that Shopify native (ITP-7-day-capped) loses.
    // MEASUREMENT ONLY — these are NOT Meta match keys and are never sent to
    // Meta. Single silent-fail try/catch; _dose_first_click_at is written LAST
    // so a bad timestamp can never drop _dose_fbclid / _dose_vid.
    try {
      var clicks = readStore(CLICK_STORE, {});
      if (clicks && clicks.fbclid) {
        body.set('attributes[_dose_fbclid]', clicks.fbclid);
      }
      // ensureVisitorId() (not a raw getCookie) is the canonical accessor — it
      // returns the SAME value browse events stamp as visitor_id, which is the
      // join key v_pixel_click_to_purchase uses to thread the purchase back to
      // its browse events.
      var browseVid = ensureVisitorId();
      if (browseVid && UUID_V4_RE.test(browseVid)) {
        body.set('attributes[_dose_vid]', browseVid);
      }
      var firstClickAt = clicks && clicks._captured_at;
      if (typeof firstClickAt === 'number' && isFinite(firstClickAt)) {
        body.set('attributes[_dose_first_click_at]', new Date(firstClickAt).toISOString());
      }
    } catch {
      // Shadow measurement keys are best-effort; the cart write proceeds.
    }

    var writeAttribute = window.fetch
      ? fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
      : Promise.resolve(null);

    return writeAttribute.then(function (response) {
      // Only flip to confirmed on a real 2xx — otherwise leave the
      // record pending so the next add_to_cart retries. response.ok
      // is true for 200-299, false for 4xx/5xx. A null response
      // (window.fetch missing) is treated as not confirmed.
      if (response && response.ok) {
        writeStore(PURCHASE_EVENT_STORE, {
          event_id: purchaseEventId,
          created_at: createdAt,
          cart_write_confirmed: true
        });
      }
      return purchaseEventId;
    }).catch(function () {
      // Rejected fetch (network failure, CORS, etc.) → leave record
      // pending. Next call retries with the same UUID.
      return purchaseEventId;
    });
  }

  // Called by the checkout-link click hook (installCheckoutHook).
  // Ensures the cart-attribute is written + fires the checkout_start
  // pixel event. Idempotent on the cart-write thanks to
  // ensurePurchaseEventIdInCart's stored-id check.
  function preallocatePurchaseEventId() {
    return ensurePurchaseEventIdInCart().then(function (purchaseEventId) {
      if (purchaseEventId) {
        track('checkout_start', { _dose_purchase_event_id: purchaseEventId });
      }
      return purchaseEventId;
    });
  }

  function installCheckoutHook() {
    document.addEventListener('click', function (event) {
      var el = event.target;
      while (el && el !== document && !(el.tagName === 'A' && el.href)) el = el.parentNode;
      if (!el || el === document || !isCheckoutHref(el.href)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.defaultPrevented) return;

      event.preventDefault();
      var href = el.href;
      preallocatePurchaseEventId().then(function () {
        window.location.href = href;
      });
    }, true);
  }

  function replayQueuedCall(call) {
    if (!Array.isArray(call) || call.length === 0) return;
    var method = call[0];
    var args = call.slice(1);
    if (method === 'track') {
      track.apply(null, args);
    } else if (method === 'identify') {
      window.dosePixel.identify.apply(window.dosePixel, args);
    } else if (method === 'checkoutStart') {
      preallocatePurchaseEventId.apply(null, args);
    }
  }

  var liveQueue = [];
  liveQueue.push = function () {
    for (var i = 0; i < arguments.length; i++) replayQueuedCall(arguments[i]);
    return Array.prototype.push.apply(this, arguments);
  };

  // Snapshot the _dose_vid cookie state BEFORE anything else can call
  // ensureVisitorId() (which is what commonFields() does for every event).
  // The fingerprint gate needs to know "cookie was absent on this page load."
  visitorCookieMissingAtLoad = getCookie(VISITOR_COOKIE) === null;

  // Kick off fingerprint compute as early as possible so the SHA-256 digest
  // resolves before the deferred initial page_view fires. No-op when gates fail.
  startFingerprintCompute();

  var dosePixelApi = {
    loaded: true,
    q: liveQueue,
    track: track,
    identify: function (identity) {
      identity = identity || {};
      return track('lead_captured', {
        email_raw: identity.email || identity.email_raw,
        phone_raw: identity.phone || identity.phone_raw,
        shopify_customer_id: identity.shopify_customer_id || shopifyCustomerId()
      });
    },
    checkoutStart: preallocatePurchaseEventId,
    disable: function () {
      // Always set the in-memory flag so this page lifetime opts out
      // immediately. Then try to persist to localStorage for future sessions.
      inMemoryDisabled = true;
      try {
        if (window.localStorage) window.localStorage.setItem(DISABLED_FLAG, '1');
      } catch {
        // localStorage may be blocked (Safari private browsing, quota
        // exceeded). Opt-out still holds for the current page via the
        // in-memory flag; it just doesn't persist across reloads.
      }
    },
    enable: function () {
      inMemoryDisabled = false;
      try {
        if (window.localStorage) window.localStorage.removeItem(DISABLED_FLAG);
      } catch {
        // ignore — see disable()
      }
    }
  };
  Object.defineProperty(dosePixelApi, 'disabled', {
    get: isDisabled,
    enumerable: true,
  });
  window.dosePixel = dosePixelApi;

  for (var i = 0; i < queuedCalls.length; i++) replayQueuedCall(queuedCalls[i]);

  installFetchAddToCartHook();
  installCheckoutHook();
  installNavigationHooks();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startInitialEventsWhenVisible);
  } else {
    startInitialEventsWhenVisible();
  }
})();
